import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  confirmDonationBatch,
  createDonationBudget,
  createDonationRequisition,
  createMarketProject,
  deleteMarketProject,
  disburseDonationBatch,
  mpP2QueryKeys,
  publishDonationBatch,
  updateDonationBudget,
  updateDonationRequisition,
  updateMarketProject,
} from '../api/marketPromotionP2.api';
import type {
  DonationBudgetPayload,
  DonationRequisitionPayload,
  MarketProjectPayload,
  PublishDonationPayload,
} from '../types/marketPromotionP2.types';

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: mpP2QueryKeys.all() });
  };
}

export function useSaveMarketProject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | null; payload: MarketProjectPayload }) =>
      id === null ? createMarketProject(payload) : updateMarketProject(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Project added' : 'Project updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the project'),
  });
}

export function useDeleteMarketProject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: number) => deleteMarketProject(id),
    onSuccess: () => {
      invalidate();
      toast.success('Project removed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the project'),
  });
}

export function useSaveDonationBudget(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | null; payload: DonationBudgetPayload }) =>
      id === null ? createDonationBudget(payload) : updateDonationBudget(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Budget added' : 'Budget updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the budget'),
  });
}

export function useSaveDonationRequisition(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | null; payload: DonationRequisitionPayload }) =>
      id === null ? createDonationRequisition(payload) : updateDonationRequisition(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Requisition raised' : 'Requisition updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the requisition'),
  });
}

export function usePublishDonationBatch(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (payload: PublishDonationPayload) => publishDonationBatch(payload),
    onSuccess: () => {
      invalidate();
      toast.success('Batch published');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to publish'),
  });
}

export function useConfirmDonationBatch(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      batchId,
      isConfirm,
      comment,
    }: {
      batchId: number;
      isConfirm: boolean;
      comment?: string;
    }) => confirmDonationBatch(batchId, isConfirm, comment),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.isConfirm ? 'Batch confirmed' : 'Batch sent back');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to record the decision'),
  });
}

export function useDisburseDonationBatch(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ batchId, comment }: { batchId: number; comment?: string }) =>
      disburseDonationBatch(batchId, comment),
    onSuccess: () => {
      invalidate();
      toast.success('Batch disbursed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to disburse'),
  });
}
